/**
 * Business rule: the 3ml sample size sells best, so it should be the
 * pre-selected default everywhere a variant picker appears (PDP, quick-add
 * modal), falling back to 2ml if a product has no 3ml. 1ml must never be
 * the default. Only applied once per fresh render — never re-applied on a
 * change the shopper triggers themselves, and never overrides a variant
 * chosen explicitly via a `?variant=` link.
 */

const PREFERRED_SIZE_PATTERNS = [/^3\s?ml$/i, /^2\s?ml$/i];
const SIZE_LIKE_PATTERN = /^\d+\s?ml$/i;

function hasExplicitVariantParam() {
  try {
    return new URLSearchParams(window.location.search).has('variant');
  } catch {
    return false;
  }
}

/**
 * Finds the radio for the preferred size (3ml, falling back to 2ml) within
 * a single <variant-picker>, without mutating anything. Returns null if the
 * picker has no size-like options, or the currently checked option already
 * satisfies the preference (nothing to change).
 * @param {Element} picker - A <variant-picker> element (connected or not).
 * @returns {HTMLInputElement | null}
 */
function findPreferredSizeRadio(picker) {
  const radios = Array.from(picker.querySelectorAll('input[type="radio"][data-option-value-id]'));
  const sizeRadios = radios.filter(
    (radio) => radio instanceof HTMLInputElement && SIZE_LIKE_PATTERN.test(radio.value.trim())
  );
  if (sizeRadios.length === 0) return null;

  const currentlyChecked = sizeRadios.find((radio) => radio.checked);
  if (currentlyChecked && PREFERRED_SIZE_PATTERNS.some((pattern) => pattern.test(currentlyChecked.value.trim()))) {
    return null;
  }

  for (const pattern of PREFERRED_SIZE_PATTERNS) {
    const target = sizeRadios.find(
      (radio) => pattern.test(radio.value.trim()) && radio.dataset.optionAvailable === 'true'
    );
    if (target) return target;
  }

  return null;
}

/**
 * Reads the variant ID the 3ml/2ml preference would select for the first
 * <variant-picker> found within scope, without mutating anything. Used to
 * decide *before* rendering whether a fetch needs to ask the server for a
 * different variant, instead of fixing it up after the wrong one already
 * painted.
 * @param {Element} scope
 * @returns {string | null}
 */
export function getPreferredVariantId(scope) {
  const picker = scope.matches?.('variant-picker') ? scope : scope.querySelector?.('variant-picker');
  if (!picker) return null;

  const target = findPreferredSizeRadio(picker);
  return target?.dataset.variantId ?? null;
}

/**
 * @param {Element} scope - Element to search for <variant-picker> instances within
 *   (or the variant-picker element itself).
 */
export function applyDefaultVariantPreference(scope) {
  if (!scope || hasExplicitVariantParam()) return;

  const pickers = scope.matches?.('variant-picker')
    ? [scope]
    : Array.from(scope.querySelectorAll?.('variant-picker') ?? []);

  for (const picker of pickers) {
    const target = findPreferredSizeRadio(picker);
    if (!target) continue;

    if (!target.checked) {
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

// This module can execute very early (e.g. pulled in transitively by
// quick-add.js, which loads before other components such as
// <sticky-add-to-cart> and <product-price> that also react to the
// synthetic "change" event dispatched below). Those custom elements only
// attach their listeners once their own module has run and upgraded them,
// so dispatching before that point means they miss the event permanently
// and stay stuck on the server-rendered first variant. Module scripts run
// while the document is still merely "interactive" - readyState only
// reaches "loading" before them, never during - so DOMContentLoaded (fired
// only once every deferred/module script, including later ones, has run)
// is the correct signal to wait for, unless it has already fired.
if (document.readyState === 'complete') {
  applyDefaultVariantPreference(document);
} else {
  document.addEventListener('DOMContentLoaded', () => applyDefaultVariantPreference(document));
}
